import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import AccessTime from "@material-ui/icons/AccessTime";
import Done from "@material-ui/icons/Done";
import DoneAll from "@material-ui/icons/DoneAll";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { blue } from "@material-ui/core/colors";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  buildHighlightedSnippet,
  meaningfulLength,
} from "../../helpers/highlightSnippet";
import {
  isGroupRemoteJid,
  resolveGroupSenderName,
} from "../../helpers/groupSenderName";

// Deve casar com o `drawerWidth` do Ticket (320) — é a largura que o
// `mainWrapperShift` devolve ao abrir. O root fica `position: absolute`
// para NÃO ocupar espaço no fluxo quando fechado (senão sobraria uma
// faixa branca à direita, já que o `marginRight` negativo do chat só
// compensa a largura de UM drawer).
const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
  drawer: {
    position: "absolute",
    top: 0,
    right: 0,
    height: "100%",
    width: drawerWidth,
    flexShrink: 0,
    zIndex: 3,
    // Fechado, o root fica sem paper visível; evita que a caixa vazia
    // capture cliques sobre a área direita do chat.
    pointerEvents: "none",
  },
  drawerPaper: {
    width: drawerWidth,
    pointerEvents: "auto",
    display: "flex",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  header: {
    display: "flex",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.contactdrawer,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: "73px",
    justifyContent: "flex-start",
  },
  searchBox: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.contactdrawer,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  content: {
    display: "flex",
    backgroundColor: theme.palette.contactdrawer,
    flexDirection: "column",
    padding: 0,
    height: "100%",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  emptyState: {
    padding: theme.spacing(3, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  resultItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  resultTopRow: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  resultSender: {
    fontWeight: 600,
    fontSize: "0.8rem",
  },
  resultDate: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  resultSnippet: {
    fontSize: "0.85rem",
    wordBreak: "break-word",
    marginTop: 2,
  },
  ackIcons: {
    fontSize: 16,
    verticalAlign: "middle",
    marginRight: 3,
  },
  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 16,
    verticalAlign: "middle",
    marginRight: 3,
  },
  ackErrorIcon: {
    color: "#f44336",
    fontSize: 16,
    verticalAlign: "middle",
    marginRight: 3,
  },
  highlight: {
    backgroundColor:
      theme.palette.type === "dark" ? "#ffd54f" : "rgba(255, 213, 79, 0.6)",
    // No escuro o texto do trecho é claro e sumiria sobre o realce.
    color: theme.palette.type === "dark" ? "#1c1c1c" : "inherit",
    borderRadius: 2,
    padding: "0 2px",
    fontWeight: 600,
  },
  ticketChip: {
    marginTop: 4,
    height: 20,
    fontSize: "0.65rem",
  },
  loadMoreWrapper: {
    display: "flex",
    justifyContent: "stretch",
    padding: theme.spacing(1),
  },
  loadMoreButton: {
    width: "100%",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString();
};

const MessageSearchDrawer = ({
  open,
  handleDrawerClose,
  ticket,
  contactId,
  onJumpToMessage,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [messages, setMessages] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // Modo de histórico da empresa (vem do backend junto com os resultados). Define
  // se a mensagem encontrada já faz parte do histórico exibido no ticket aberto.
  const [historyMode, setHistoryMode] = useState("contact");

  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);
  // A busca anterior é abortada quando outra começa, quando o painel fecha e no
  // unmount — resultado que ninguém vai ver não deve ocupar conexão.
  const searchAbortRef = useRef(null);

  const trimmedSearch = searchParam.trim();

  // Escopo da busca: por ticket (header do ticket) ou por contato (aba
  // Contatos, sem ticket). O endpoint de contato busca todo o histórico.
  const searchEndpoint = ticket?.id
    ? `/messages/${ticket.id}/search`
    : contactId
    ? `/messages/contact/${contactId}/search`
    : null;

  // Conta apenas letras/números (ignora espaços, pontuação e símbolos) para
  // decidir o gatilho da busca — inicia só a partir de 2 caracteres válidos.
  const canSearch = meaningfulLength(trimmedSearch) >= 2;

  const fetchMessages = useCallback(
    async (term, page) => {
      if (!searchEndpoint || !term) return;
      const seq = ++requestSeqRef.current;
      searchAbortRef.current?.abort();
      const abortController = new AbortController();
      searchAbortRef.current = abortController;
      setLoading(true);
      try {
        const { data } = await api.get(searchEndpoint, {
          params: { searchParam: term, pageNumber: page },
          signal: abortController.signal,
        });
        // Ignora respostas de buscas antigas (out-of-order).
        if (seq !== requestSeqRef.current) return;
        setMessages((prev) =>
          page === 1 ? data.messages : [...prev, ...data.messages]
        );
        setHasMore(Boolean(data.hasMore));
        setHistoryMode(data.historyMode || "contact");
        setSearched(true);
      } catch (err) {
        if (seq === requestSeqRef.current) {
          toastError(err);
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [searchEndpoint]
  );

  // Debounce na digitação; dispara busca da página 1.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!canSearch) {
      searchAbortRef.current?.abort();
      setMessages([]);
      setHasMore(false);
      setSearched(false);
      setPageNumber(1);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      setPageNumber(1);
      fetchMessages(trimmedSearch, 1);
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trimmedSearch, canSearch, fetchMessages]);

  useEffect(() => () => searchAbortRef.current?.abort(), []);

  // Limpa o estado ao fechar o painel.
  useEffect(() => {
    if (!open) {
      searchAbortRef.current?.abort();
      setSearchParam("");
      setMessages([]);
      setHasMore(false);
      setSearched(false);
      setPageNumber(1);
    }
  }, [open]);

  const handleLoadMore = () => {
    const next = pageNumber + 1;
    setPageNumber(next);
    fetchMessages(trimmedSearch, next);
  };

  // Status de entrega (ticks) para mensagens enviadas, no padrão WhatsApp.
  const renderMessageAck = (message) => {
    if (!message?.fromMe) return null;
    const ack = Number(message.ack);

    if (ack === -1) {
      return <ErrorOutline fontSize="small" className={classes.ackErrorIcon} />;
    }
    if (ack === 0 || ack === 1) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (ack === 4 || ack === 5) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
    return null;
  };

  const getSenderLabel = (message) => {
    if (message?.fromMe) return i18n.t("messageSearchDrawer.you");

    // Em grupo o `contact` da mensagem é o próprio grupo; quem escreveu vem do
    // participante, na mesma ordem que o histórico resolve.
    if (isGroupRemoteJid(message?.remoteJid) || ticket?.isGroup) {
      const groupSender = resolveGroupSenderName(message);
      if (groupSender) return groupSender;
    }

    return (
      message?.contact?.name ||
      ticket?.contact?.name ||
      i18n.t("messageSearchDrawer.contact")
    );
  };

  const handleResultClick = async (message) => {
    if (!message?.id) return;

    // Modo contato (aba Contatos, sem ticket fixo): a lista já exibe todo o
    // histórico do contato, então sempre pulamos in-place.
    if (!ticket?.id) {
      if (typeof handleDrawerClose === "function") handleDrawerClose();
      if (typeof onJumpToMessage === "function") {
        setTimeout(() => onJumpToMessage(String(message.id)), 120);
      }
      return;
    }

    const currentTicketId = ticket?.id || null;
    const messageTicketId = message?.ticketId || message?.ticket?.id || null;

    const isSameTicket =
      currentTicketId &&
      messageTicketId &&
      Number(messageTicketId) === Number(currentTicketId);

    // Nos modos de histórico 'contact' e 'whatsapp' o ticket aberto já exibe as
    // mensagens dos tickets anteriores do mesmo contato — então uma mensagem
    // marcada como "Ticket anterior" está no histórico que já está na tela.
    // Trocar de ticket nesse caso só reiniciaria a navegação à toa.
    const isInCurrentHistory =
      isSameTicket ||
      historyMode === "contact" ||
      (historyMode === "whatsapp" &&
        Number(message?.ticket?.whatsappId) === Number(ticket?.whatsappId));

    if (isInCurrentHistory) {
      if (typeof handleDrawerClose === "function") handleDrawerClose();
      if (typeof onJumpToMessage === "function") {
        setTimeout(() => onJumpToMessage(String(message.id)), 120);
      }
      return;
    }

    // Modo 'ticket': cada ticket tem histórico próprio, então é preciso abrir o
    // ticket de origem da mensagem para poder exibi-la.
    let targetTicketUuid = message?.ticket?.uuid || null;
    const targetTicketId = messageTicketId;

    if (!targetTicketUuid && targetTicketId) {
      try {
        const { data } = await api.get(`/tickets/${targetTicketId}`);
        targetTicketUuid = data?.uuid || null;
      } catch (_err) {
        targetTicketUuid = null;
      }
    }

    if (!targetTicketUuid) {
      // fallback: tenta pular no ticket atual mesmo assim
      if (typeof onJumpToMessage === "function") {
        if (typeof handleDrawerClose === "function") handleDrawerClose();
        setTimeout(() => onJumpToMessage(String(message.id)), 120);
      }
      return;
    }

    if (typeof handleDrawerClose === "function") handleDrawerClose();
    history.push(`/tickets/${targetTicketUuid}`, {
      jumpToMessageId: String(message.id),
      jumpToMessageTs: Date.now(),
    });
  };

  const renderResults = () => {
    if (loading && messages.length === 0) {
      return (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={24} />
        </div>
      );
    }

    if (!canSearch) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.t("messageSearchDrawer.prompt")}
          </Typography>
        </div>
      );
    }

    if (searched && messages.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.t("messageSearchDrawer.noResults")}
          </Typography>
        </div>
      );
    }

    return (
      <List disablePadding>
        {messages.map((message) => {
          const isOtherTicket =
            ticket?.id &&
            (message?.ticketId || message?.ticket?.id) &&
            Number(message?.ticketId || message?.ticket?.id) !== Number(ticket.id);
          return (
            <React.Fragment key={message.id}>
              <ListItem
                button
                onClick={() => handleResultClick(message)}
                className={classes.resultItem}
              >
                <div className={classes.resultTopRow}>
                  <span className={classes.resultSender}>
                    {getSenderLabel(message)}
                  </span>
                  <span className={classes.resultDate}>
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <ListItemText
                  disableTypography
                  primary={
                    <span className={classes.resultSnippet}>
                      {renderMessageAck(message)}
                      {buildHighlightedSnippet(
                        message.body,
                        trimmedSearch,
                        classes.highlight
                      )}
                    </span>
                  }
                />
                {isOtherTicket && (
                  <Chip
                    size="small"
                    variant="outlined"
                    className={classes.ticketChip}
                    label={`${i18n.t("messageSearchDrawer.previousTicket")} #${message?.ticketId || message?.ticket?.id}`}
                  />
                )}
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}

        {hasMore && (
          <div className={classes.loadMoreWrapper}>
            <Button
              size="small"
              onClick={handleLoadMore}
              disabled={loading}
              color="primary"
              fullWidth
              className={classes.loadMoreButton}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                i18n.t("messageSearchDrawer.loadMore")
              )}
            </Button>
          </div>
        )}
      </List>
    );
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" },
      }}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <IconButton onClick={handleDrawerClose}>
          <CloseIcon />
        </IconButton>
        <Typography style={{ justifySelf: "center" }}>
          {i18n.t("messageSearchDrawer.title")}
        </Typography>
      </div>

      <div className={classes.searchBox}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          autoFocus
          placeholder={i18n.t("messageSearchDrawer.placeholder")}
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.content}>{renderResults()}</div>
    </Drawer>
  );
};

export default MessageSearchDrawer;
